import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import Layout, { PageHeader } from "@/components/Layout";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Plug, Trash2, Loader2, Radio, ShieldCheck, RefreshCw, Pencil, ExternalLink,
  Paperclip, X, Power, CheckCircle2, Circle, Zap, AlertTriangle
} from "lucide-react";

const IN = "mt-1 w-full h-10 px-3 bg-white border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-1 focus:ring-blue-600";
const LBL = "text-xs font-bold tracking-wide uppercase text-slate-500";

export default function Brokers() {
  const [available, setAvailable] = useState([]);
  const [connections, setConnections] = useState([]);
  const [dlg, setDlg] = useState(null);            // { broker, editId }
  const [form, setForm] = useState({ label: "", exchanges: [], creds: {}, attachments: [] });
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [params, setParams] = useSearchParams();

  const token = localStorage.getItem("kiran_token");
  const load = () => {
    api.get("/brokers/available").then((r) => setAvailable(r.data));
    api.get("/brokers").then((r) => setConnections(r.data));
  };
  useEffect(load, []);
  useEffect(() => {
    const k = params.get("kite");
    const icici = params.get("icici");

    if (k === "connected") toast.success("Zerodha live session active for today");
    else if (k === "error") toast.error("Zerodha login failed");

    if (icici === "connected") toast.success("ICICI Breeze live session active for today");
    else if (icici === "error") toast.error("ICICI Breeze login failed");

    if (k || icici) {
      if (k) params.delete("kite");
      if (icici) params.delete("icici");
      setParams(params);
      load();
    }
  }, []); // eslint-disable-line

  const openCreate = (b) => { setForm({ label: b.name, exchanges: b.default_exchanges || [], creds: {}, attachments: [] }); setDlg({ broker: b, editId: null }); };
  const openEdit = (c) => {
    const b = available.find((x) => x.id === c.broker);
    setForm({ label: c.label, exchanges: c.exchanges || [], creds: {}, attachments: c.attachments || [] });
    setDlg({ broker: b, editId: c.id });
  };

  const toggleExchange = (ex) => setForm((f) => ({ ...f, exchanges: f.exchanges.includes(ex) ? f.exchanges.filter((e) => e !== ex) : [...f.exchanges, ex] }));

  const uploadDoc = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, attachments: [...f.attachments, data.id] }));
      toast.success("Document attached");
    } catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); }
  };

  const save = async () => {
    setBusy(true);
    try {
      if (dlg.editId) {
        await api.put(`/brokers/${dlg.editId}`, { label: form.label, exchanges: form.exchanges, credentials: form.creds, attachments: form.attachments });
        toast.success("Broker updated");
      } else {
        await api.post("/brokers", { broker: dlg.broker.id, label: form.label, exchanges: form.exchanges, credentials: form.creds, attachments: form.attachments });
        toast.success(`${dlg.broker.name} connected`);
      }
      setDlg(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const renew = async (id) => { await api.post(`/brokers/${id}/renew`); toast.success("Session renewed"); load(); };
  const verify = async (id) => {
    try {
      const { data } = await api.post(`/brokers/${id}/verify`);
      setResult({ title: "Access Token Status", ...data });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Verification not supported for this broker"); }
  };
  const testSignal = async (id) => {
    try {
      const { data } = await api.post(`/brokers/${id}/test-signal`);
      setResult({ title: "Broker Test Signal", ...data });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Test signal failed"); }
  };
  const toggle = async (id) => { await api.post(`/brokers/${id}/toggle`); load(); };
  const remove = async (id) => { await api.delete(`/brokers/${id}`); toast.success("Broker deleted"); load(); };

  const kiteLogin = async (id) => {
    try { const { data } = await api.post(`/brokers/${id}/kite/login-url`); window.location.href = data.url; }
    catch (e) { toast.error(e.response?.data?.detail || "Add your Kite API Key & Secret first (Edit the broker)."); }
  };

  const iciciLogin = async (id) => {
    try {
      const { data } = await api.post(`/brokers/${id}/icici/login-url`);
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Add your ICICI Breeze API Key first (Edit the broker).");
    }
  };

  return (
    <Layout>
      <PageHeader title="Broker Connections" subtitle="Add your broker with its API key & secret, then login to generate the access token" />
      <div className="min-w-0 p-4 sm:p-6 lg:p-8">
        {/* Available brokers */}
        <h3 className="text-xs font-bold tracking-[0.15em] uppercase text-slate-500 mb-4">Add a Broker</h3>
        <div className="grid grid-cols-1 min-[380px]:grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-10 min-w-0">
          {available.map((b) => (
            <button key={b.id} data-testid={`broker-connect-${b.id}`} onClick={() => openCreate(b)}
              className="text-left p-4 bg-white border-2 border-slate-200 rounded-none transition-all duration-150 hover:border-slate-900 hover:shadow-[4px_4px_0px_0px_rgba(15,23,42,1)]">
              <div className="flex items-center justify-between mb-2">
                <div className="w-8 h-8 bg-blue-600 text-white font-heading font-bold flex items-center justify-center">{b.icon}</div>
                {b.live_supported && <span className="text-[9px] font-mono bg-emerald-50 text-emerald-700 px-1.5 py-0.5">LIVE</span>}
              </div>
              <div className="font-heading font-bold text-sm tracking-tight">{b.name}</div>
              <div className="text-[11px] text-slate-400 mt-0.5 font-mono truncate">{b.exchanges.join(", ")}</div>
            </button>
          ))}
        </div>

        {/* Connected brokers - Tradetron style cards */}
        <h3 className="text-xs font-bold tracking-[0.15em] uppercase text-slate-500 mb-4">Your Connections</h3>
        {connections.length === 0 ? (
          <div className="border border-dashed border-slate-300 p-10 text-center text-slate-400 text-sm">No brokers connected yet. Add one above.</div>
        ) : (
          <div className="space-y-5">
            {connections.map((c) => (
              <div key={c.id} className="border border-slate-200 bg-white" data-testid={`connection-${c.id}`}>
                {/* header */}
                <div className="flex flex-col items-stretch gap-3 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6 border-b border-slate-100">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-600 text-white font-heading font-bold flex items-center justify-center">{c.icon}</div>
                    <div>
                      <div className="font-heading font-bold text-lg tracking-tight leading-none">{c.label}</div>
                      <div className="text-xs text-slate-500 font-mono mt-1">{(c.exchanges || []).join(", ")}</div>
                    </div>
                  </div>
                  <div className="flex min-w-0 flex-wrap items-center justify-end gap-2">
                    {c.token_status && (
                      <span data-testid={`token-badge-${c.id}`} className={`text-[10px] font-mono px-2 py-1 ${c.token_status === "active" ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"}`}>
                        TOKEN {c.token_status.toUpperCase()}
                      </span>
                    )}
                    <button data-testid={`edit-${c.id}`} onClick={() => openEdit(c)} title="Edit"
                      className="w-9 h-9 flex items-center justify-center border border-slate-300 hover:border-slate-900 transition-colors"><Pencil className="w-4 h-4" /></button>
                    <button data-testid={`delete-${c.id}`} onClick={() => remove(c.id)} title="Delete"
                      className="w-9 h-9 flex items-center justify-center border border-slate-300 hover:border-rose-500 hover:text-rose-500 transition-colors"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
                {/* status row */}
                <div className="flex flex-col items-stretch gap-3 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6 border-b border-slate-100">
                  <div className="flex min-w-0 flex-wrap items-center gap-3 sm:gap-6">
                    <span className={`inline-flex items-center gap-2 text-sm font-semibold px-3 py-1 ${c.status === "connected" ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                      {c.status === "connected" ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Circle className="w-3.5 h-3.5" />}
                      {c.status === "connected" ? "Active IP" : "Disconnected"}
                    </span>
                    <span className="font-mono font-bold text-slate-800">{c.active_ip || "—"}</span>
                    <span className="text-sm text-slate-500">Expires: <span className="font-mono font-semibold text-slate-800">{c.ip_expires || "—"}</span></span>
                  </div>
                  <div className="flex min-w-0 flex-wrap items-center justify-end gap-2">
                    {c.broker === "zerodha" && (
                      <button data-testid={`zerodha-login-${c.id}`} onClick={() => kiteLogin(c.id)}
                        className="h-9 px-4 bg-slate-900 text-white text-xs font-semibold flex items-center gap-1 hover:bg-slate-800 transition-colors">
                        <ShieldCheck className="w-3.5 h-3.5" /> Login to Zerodha
                      </button>
                    )}

                    {c.broker === "icici" && (
                      <button data-testid={`icici-login-${c.id}`} onClick={() => iciciLogin(c.id)}
                        className="h-9 px-4 bg-slate-900 text-white text-xs font-semibold flex items-center gap-1 hover:bg-slate-800 transition-colors">
                        <ShieldCheck className="w-3.5 h-3.5" /> Login to ICICI
                      </button>
                    )}
                    <button data-testid={`verify-${c.id}`} onClick={() => verify(c.id)} title="Check if token is working"
                      className="h-9 px-3 text-xs font-semibold border border-slate-300 flex items-center gap-1 hover:border-emerald-600 hover:text-emerald-600 transition-colors">
                      <ShieldCheck className="w-3.5 h-3.5" /> Verify Token
                    </button>
                    <button data-testid={`test-signal-${c.id}`} onClick={() => testSignal(c.id)} title="Fire a one-time test signal to this broker"
                      className="h-9 px-3 text-xs font-semibold border border-slate-300 flex items-center gap-1 hover:border-blue-600 hover:text-blue-600 transition-colors">
                      <Zap className="w-3.5 h-3.5" /> Test Signal
                    </button>
                    <button data-testid={`disconnect-${c.id}`} onClick={() => toggle(c.id)}
                      className="h-9 px-3 text-xs font-semibold border border-slate-300 flex items-center gap-1 hover:border-slate-900 transition-colors">
                      <Power className="w-3.5 h-3.5" /> {c.status === "connected" ? "Disconnect" : "Connect"}
                    </button>
                    <button data-testid={`renew-${c.id}`} onClick={() => renew(c.id)}
                      className="h-9 px-4 bg-blue-600 text-white text-sm font-semibold flex items-center gap-2 hover:bg-blue-700 transition-colors">
                      <RefreshCw className="w-4 h-4" /> Renew
                    </button>
                  </div>
                </div>
                {/* info + daily auth link */}
                <div className="min-w-0 px-4 py-4 sm:px-6">
                  <div className="text-xs text-slate-400 mb-1">Last Updated: <span className="font-mono text-slate-600">{c.last_updated ? new Date(c.last_updated).toLocaleString("en-IN") : "—"}</span></div>
                  <div className="text-xs font-bold tracking-wide uppercase text-slate-500 mt-3 mb-1">Info</div>
                  <p className="text-sm text-slate-600">{c.auth_note}</p>
                  {c.auth_url && (
                    <a data-testid={`auth-link-${c.id}`} href={c.auth_url} target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 mt-2 text-sm text-blue-600 font-semibold hover:underline break-all">
                      {c.auth_url} <ExternalLink className="w-3.5 h-3.5 shrink-0" />
                    </a>
                  )}
                  {!c.auth_url && c.broker === "zerodha" && (
                    <p className="text-xs text-slate-600 bg-slate-50 border border-slate-200 px-2 py-1.5 mt-2">
                      Enter your Kite <b>API Key</b> & <b>API Secret</b> (Edit broker), then click <b>Login to Zerodha</b> to generate today's access token. In your Kite app, set the Redirect URL to <span className="font-mono">{process.env.REACT_APP_BACKEND_URL}/api/brokers/kite/callback</span>.
                    </p>
                  )}

                  {c.broker === "icici" && (
                    <p className="text-xs text-slate-600 bg-slate-50 border border-slate-200 px-2 py-1.5 mt-2">
                      Enter your ICICI Breeze <b>API Key</b> & <b>API Secret</b> (Edit broker), then click <b>Login to ICICI</b> to generate today's Breeze session. In your ICICI Breeze app, set the Redirect URL to <span className="font-mono">https://nse.kiranalgo.in/api/brokers/icici/callback</span>.
                    </p>
                  )}
                  {(c.attachments || []).length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {c.attachments.map((fid) => (
                        <a key={fid} href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${fid}/download?auth=${token}`} target="_blank" rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-xs px-2 py-1 bg-slate-100 hover:bg-slate-200 transition-colors font-mono">
                          <Paperclip className="w-3 h-3" /> document
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Config dialog */}
      <Dialog open={!!dlg} onOpenChange={(v) => !v && setDlg(null)}>
        <DialogContent className="w-[calc(100vw-2rem)] max-w-lg max-h-[90dvh] overflow-y-auto rounded-none">
          <DialogHeader><DialogTitle className="font-heading">{dlg?.editId ? "Edit" : "Connect"} {dlg?.broker?.name}</DialogTitle></DialogHeader>
          {dlg?.broker && (
            <div className="space-y-4 pt-2">
              {dlg.broker.setup_link && (
                <a href={dlg.broker.setup_link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-sm text-blue-600 hover:underline">
                  Setup instructions <ExternalLink className="w-3.5 h-3.5" />
                </a>
              )}
              <div>
                <label className={LBL}>Connection Label</label>
                <input data-testid="broker-label" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} className={IN} />
              </div>
              <div>
                <label className={LBL}>Exchanges</label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {dlg.broker.exchanges.map((ex) => (
                    <button key={ex} type="button" data-testid={`exchange-${ex}`} onClick={() => toggleExchange(ex)}
                      className={`px-3 h-9 text-xs font-semibold border transition-colors ${form.exchanges.includes(ex) ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-500 border-slate-300"}`}>
                      {ex} {form.exchanges.includes(ex) && <X className="w-3 h-3 inline ml-1" />}
                    </button>
                  ))}
                </div>
              </div>
              <div className="border-t border-slate-100 pt-3" autoComplete="off">
                <div className="text-xs font-bold tracking-wide uppercase text-slate-500 mb-2">Connect to Broker</div>
                <div className="grid grid-cols-2 gap-3">
                  {dlg.broker.fields.map((f) => (
                    <div key={f.name} className={dlg.broker.fields.length % 2 !== 0 && f === dlg.broker.fields[dlg.broker.fields.length - 1] ? "col-span-2" : ""}>
                      <label className={LBL}>{f.label}</label>
                      <input data-testid={`cred-${f.name}`}
                        name={`kiran_credential_${dlg.broker.id}_${f.name}_${Date.now()}`}
                        autoComplete="new-password"
                        data-lpignore="true"
                        data-1p-ignore="true"
                        data-bwignore="true"
                        autoCapitalize="none"
                        spellCheck={false}
                        type={f.secret ? "password" : "text"}
                        readOnly={dlg.editId && !form.creds[f.name]}
                        value={form.creds[f.name] || ""}
                        onFocus={(e) => {
                          if (dlg.editId && !form.creds[f.name]) {
                            e.currentTarget.readOnly = false;
                          }
                        }}
                        onChange={(e) => setForm({ ...form, creds: { ...form.creds, [f.name]: e.target.value } })}
                        placeholder={dlg.editId ? "•••• (unchanged)" : ""} className={IN} />
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <label className={LBL}>Attach Setup Document (optional)</label>
                <div className="mt-1 flex items-center gap-3">
                  <label className="h-10 px-4 border border-slate-300 flex items-center gap-2 text-sm cursor-pointer hover:border-slate-900 transition-colors">
                    <Paperclip className="w-4 h-4" /> {uploading ? "Uploading…" : "Choose file"}
                    <input data-testid="attach-file" type="file" className="hidden" onChange={uploadDoc}
                      accept=".png,.jpg,.jpeg,.webp,.pdf,.csv,.json,.txt" disabled={uploading} />
                  </label>
                  {form.attachments.length > 0 && <span className="text-xs font-mono text-slate-500">{form.attachments.length} attached</span>}
                </div>
              </div>
              {dlg.broker.auth_note && (
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 px-3 py-2">{dlg.broker.auth_note}</p>
              )}
              <button data-testid="submit-broker-connect" onClick={save} disabled={busy}
                className="w-full h-10 bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
                {busy && <Loader2 className="w-4 h-4 animate-spin" />} {dlg.editId ? "Save Changes" : "Save & Connect"}
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!result} onOpenChange={(v) => !v && setResult(null)}>
        <DialogContent className="rounded-none" data-testid="broker-result-dialog">
          <DialogHeader><DialogTitle className="font-heading flex items-center gap-2">
            {result?.valid || result?.ok
              ? <><CheckCircle2 className="w-5 h-5 text-emerald-600" /> {result?.title || "Result"}</>
              : <><AlertTriangle className="w-5 h-5 text-rose-500" /> {result?.title || "Result"}</>}
          </DialogTitle></DialogHeader>
          <div className="pt-2 space-y-3">
            <div className={`text-sm px-3 py-2 border ${result?.valid || result?.ok ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-rose-50 border-rose-200 text-rose-700"}`}>
              {(result?.valid || result?.ok) ? "✓ " : "✗ "}{result?.message || result?.error || (result?.valid ? "Token is valid" : "Token invalid / expired")}
            </div>
            <dl className="text-sm border border-slate-200 divide-y divide-slate-100">
              {[
                ["Broker", result?.broker],
                ["Routed", result?.routed],
                ["Token Validity", result?.token_validity],
                ["Client ID", result?.dhan_client_id],
                ["Available Balance", result?.available_balance != null ? `₹${Number(result.available_balance).toLocaleString("en-IN")}` : null],
                ["Error Code", result?.error && (result?.valid || result?.ok) ? null : result?.error],
              ].filter(([, v]) => v != null && v !== "").map(([k, v]) => (
                <div key={k} className="flex justify-between px-3 py-2">
                  <dt className="text-slate-500">{k}</dt><dd className="font-mono text-slate-800">{String(v)}</dd>
                </div>
              ))}
            </dl>
            {result?.routed === "simulated" && (
              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 px-3 py-2">
                This broker's live adapter isn't wired yet, so the test is simulated. Dhan & Zerodha run real checks.
              </p>
            )}
            <button data-testid="broker-result-close" onClick={() => setResult(null)}
              className="w-full h-10 bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 transition-colors">Close</button>
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
